package xim.poc.tools

import js.typedarrays.Uint8Array
import web.dom.document
import web.html.HTMLBodyElement
import web.html.HTMLButtonElement
import web.html.HTMLElement
import xim.poc.ActorContext
import xim.poc.ActorManager
import xim.poc.EffectManager
import xim.poc.audio.AudioManager
import xim.poc.browser.DatLoader
import xim.poc.browser.DatWrapper
import xim.poc.browser.ParserContext
import xim.poc.browser.SoundPlayer
import xim.poc.game.AttackContext
import xim.poc.game.AttackContexts
import xim.resource.*
import xim.resource.table.FileTableManager

object ExamineTool {

    fun examine(input: String) {
        if (input.startsWith("ROM")) {
            examinePath(input)
        } else {
            val fileTableIndex = input.toIntOrNull(0x10)
            val path = FileTableManager.getFilePath(fileTableIndex) ?: return
            examinePath(path)
        }
    }

    private fun examinePath(path: String) {
        val wrapper = DatLoader.load(path, parserContext = ParserContext(requiredResource = false))
        wrapper.onReady { Examine().onReady(it) }
    }

}

private class Examine {

    fun onReady(wrapper: DatWrapper) {
        val buffer = wrapper.getAsBuffer()
        val headers = DatParser.getContents(wrapper.resourceName, Uint8Array(buffer))

        val root = wrapper.getAsResource()

        val container = document.createElement("div")
        container.classList.add("tree-describe")
        container.style.left = "600px"
        container.style.top = "80px"
        enableDrag(container)

        val body = (document.body as HTMLBodyElement)
        body.appendChild(container)

        val title = document.createElement("h4")
        title.innerText = wrapper.resourceName
        title.style.display = "inline"
        container.appendChild(title)

        val close = document.createElement("button") as HTMLButtonElement
        close.innerText = "X"
        close.style.marginLeft = "80px"
        close.onclick = { container.remove(); TexturePreviewer.forceEnable(null) }
        container.appendChild(close)

        val contentContainer = document.createElement("div")
        contentContainer.classList.add("tree-body")
        container.appendChild(contentContainer)

        var currentContainer = contentContainer
        var currentDirectory: DirectoryResource? = null

        for (header in headers) {
            val id = header.sectionId.id.replace(Char(0), ' ')

            val elementContainer = document.createElement("div")
            currentContainer.appendChild(elementContainer)

            val summary = document.createElement("div")
            summary.innerText = "[$id] [@${header.sectionStartPosition.toString(0x10)}] ${header.sectionType.displayName()}"
            summary.style.marginLeft = "8px"
            summary.style.whiteSpaceCollapse = "preserve-spaces"
            summary.style.cursor = "pointer"
            elementContainer.appendChild(summary)

            val content = document.createElement("div")
            content.style.marginLeft = "30px"
            content.style.display = "none"
            elementContainer.appendChild(content)

            var hidden = true
            summary.onclick = {
                if (hidden) {
                    hidden = false
                    content.style.display = "block"
                } else {
                    hidden = true
                    content.style.display = "none"
                }
            }

            if (header.sectionType == SectionType.S01_Directory) {
                content.classList.add("branch-content")
                currentContainer = content
                currentDirectory = currentDirectory?.getSubDirectory(header.sectionId) ?: root
            } else if (header.sectionType == SectionType.S00_End) {
                currentContainer = currentContainer.parentElement?.parentElement ?: container
                currentDirectory = currentDirectory?.parent ?: root
            } else {
                content.classList.add("leaf-content")
                content(content, header, currentDirectory!!.getChildAs(header.sectionId, header.sectionType.resourceType))
            }
        }
    }

    private fun enableDrag(container: HTMLElement) {
        container.draggable = true

        var dragStartX = 0
        var dragStartY = 0

        container.ondragstart = {
            dragStartX = it.clientX
            dragStartY = it.clientY
        }

        container.ondragend = {
            val dX = it.clientX - dragStartX
            val dY = it.clientY - dragStartY

            val x = container.style.left.dropLast(2).toIntOrNull() ?: 0
            val y = container.style.top.dropLast(2).toIntOrNull() ?: 0

            container.style.left = "${x + dX}px"
            container.style.top = "${y + dY}px"
        }
    }

    private fun content(container: HTMLElement, header: SectionHeader, entry: DatEntry) {
        if (entry !is DatResource) { return }

        when (entry) {
            is EffectResource -> describe(container, entry)
            is EffectRoutineResource -> describe(container, entry)
            is KeyFrameResource -> describe(container, entry)
            is SoundPointerResource -> describe(container, entry)
            is TextureResource -> describe(container, entry)
            else -> container.innerText = "(Not yet described)"
        }
    }

    private fun describe(container: HTMLElement, keyFrameResource: KeyFrameResource) {
        val data = keyFrameResource.particleKeyFrameData.data
        for (entry in data) {
            val span = document.createElement("span")
            span.innerText = "Progress: ${entry.time}, Value: ${entry.value}\n"
            container.appendChild(span)
        }
    }

    private fun describe(container: HTMLElement, resource: SoundPointerResource) {
        val soundFile = AudioManager.toSoundFileName(resource.soundId)

        val button = document.createElement("button") as HTMLButtonElement
        button.innerText = "Play $soundFile"
        container.appendChild(button)

        button.onclick = {
            SoundPlayer(soundFile, AudioManager.volumeSettings.systemSoundVolume, id = resource.soundId, loop = false).play()
        }
    }

    private fun describe(container: HTMLElement, resource: EffectRoutineResource) {
        document.createElement("span").also {
            it.innerText = "Run All (Player)"
            it.onclick = { ActorManager.player().enqueueRoutine { resource } }
            it.classList.add("underline")
            container.appendChild(it)
        }

        container.appendChild(document.createElement("br"))
        container.appendChild(document.createElement("br"))

        val playableEffectTypes = setOf(
            LinkedEffectRoutine::class,
            ParticleGeneratorRoutine::class,
            SoundEffectRoutine::class,
        )

        for (effect in resource.effectRoutineDefinition.effects) {
            val txt = document.createElement("div").also { container.appendChild(it) }
            txt.innerText = effect.toString() + "\n"
            txt.style.marginBottom = "4px"

            if (playableEffectTypes.contains(effect::class)) {
                val dummyRoutine = EffectRoutineResource(DatId.zero, EffectRoutineDefinition().also { it.effects += effect })
                dummyRoutine.localDir = resource.localDir

                txt.onclick = {
                    val actor = ActorManager.player()

                    val effectContext = AttackContext(appearanceState = actor.state.appearanceState, appearanceCurrentDisplayState = actor.displayAppearanceState)
                    val target = actor.target ?: actor.id
                    val context = ActorContext(originalActor = actor.id, primaryTargetId = actor.target ?: actor.id, attackContexts = AttackContexts.single(target, effectContext))

                    EffectManager.registerActorRoutine(actor = actor, actorContext = context, dummyRoutine)
                }

                txt.classList.add("underline")
                txt.style.cursor = "pointer"
                txt.title = "Click to run the effect (Player -> Player target)"
            }
        }

        if (resource.effectRoutineDefinition.completionEffects.size <= 1) { return }

        document.createElement("h4").also {
            it.style.margin = "0px"
            it.innerText = "On-Complete Effects"
            container.appendChild(it)
        }

        for (effect in resource.effectRoutineDefinition.completionEffects) {
            document.createElement("span").also {
                it.innerText = effect.toString() + "\n"
                container.appendChild(it)
            }
        }
    }

    private fun describe(container: HTMLElement, resource: TextureResource) {
        val button = document.createElement("button")
        button.innerText = "Toggle Preview"
        container.appendChild(button)

        TexturePreviewer.register(resource, force = true)
        button.onclick = { TexturePreviewer.forceEnable(resource) }
    }

    private fun describe(container: HTMLElement, resource: EffectResource) {
        val generator = resource.particleGenerator

        document.createElement("h4").also {
            it.style.margin = "0px"
            it.innerText = "Generator Config"
            container.appendChild(it)
        }

        document.createElement("div").also {
            it.style.whiteSpaceCollapse = "preserve-spaces"
            it.innerText += """
                Attach type:     ${generator.attachType}
                Attach joints:   ${generator.attachedJoint0}, ${generator.attachedJoint1}
                Attach oriented: ${generator.attachSourceOriented}
                
                Actor-size scale:      ${generator.actorScaleParams.scaleSize} ${generator.actorScaleParams.scaleSizeAmount}
                Actor-position scale:  ${generator.actorScaleParams.scalePosition} ${generator.actorScaleParams.scalePositionAmount}
                
                Frames/emission:    ${generator.framesPerEmission}±${generator.emissionVariance}
                Particles/emission: ${generator.particlesPerEmission + 1}
 
                Environment: ${generator.environmentId?.id ?: "N/A"}
                Auto-run:    ${generator.autoRun}
                Batched:     ${generator.batched}
                Singleton:   ${generator.continuousSingleton}                
            """.trimIndent()

            container.appendChild(it)
        }

        document.createElement("h4").also {
            it.style.marginBottom = "0px"
            it.innerText = "Generator Updaters"
            container.appendChild(it)
        }

        resource.particleGenerator.generatorUpdaters.forEach {
            val c = document.createElement("div")
            c.innerText = it::class.simpleName ?: ""
            container.appendChild(c)
        }

        document.createElement("h4").also {
            it.style.marginBottom = "0px"
            it.innerText = "Particle Initializers"
            container.appendChild(it)
        }

        resource.particleGenerator.initializers.forEach {
            val c = document.createElement("div")
            c.innerText = it::class.simpleName ?: ""
            container.appendChild(c)
        }

        document.createElement("h4").also {
            it.style.marginBottom = "0px"
            it.innerText = "Particle Updaters"
            container.appendChild(it)
        }

        resource.particleGenerator.updaters.forEach {
            val c = document.createElement("div")
            c.innerText = it::class.simpleName ?: ""
            container.appendChild(c)
        }

        document.createElement("h4").also {
            it.style.marginBottom = "0px"
            it.innerText = "Particle Finalizers"
            container.appendChild(it)
        }

        resource.particleGenerator.expirationHandlers.forEach {
            val c = document.createElement("div")
            c.innerText = it::class.simpleName ?: ""
            container.appendChild(c)
        }
    }

}

private fun SectionType.displayName(): String {
    return when (this) {
        SectionType.S00_End -> "Directory End"
        SectionType.S01_Directory -> "Directory"
        SectionType.S04_Table -> "Table"
        SectionType.S05_ParticleGenerator -> "Particle Generator"
        SectionType.S06_Route -> "Route"
        SectionType.S07_EffectRoutine -> "Routine"
        SectionType.S19_ParticleKeyFrameData -> "Particle Key Frame"
        SectionType.S1C_ZoneDef -> "Zone Definition"
        SectionType.S1F_ParticleMesh -> "Particle Mesh (Basic)"
        SectionType.S20_Texture -> "Texture"
        SectionType.S21_SpriteSheetMesh -> "Particle Mesh (Sprite Sheet)"
        SectionType.S25_WeightedMesh -> "Particle Mesh (Weighted)"
        SectionType.S29_Skeleton -> "Actor Skeleton"
        SectionType.S2A_SkeletonMesh -> "Skeleton Mesh"
        SectionType.S2B_SkeletonAnimation -> "Skeleton Animation"
        SectionType.S2E_ZoneMesh -> "Zone Mesh"
        SectionType.S2F_Environment -> "Environment Settings"
        SectionType.S30_UiMenu -> "Ui Menu"
        SectionType.S31_UiElementGroup -> "Ui Element Group"
        SectionType.S36_ZoneInteractions -> "Zone Interaction"
        SectionType.S3E_PointList -> "Point List"
        SectionType.S3D_SoundEffectPointer -> "Sound Effect Pointer"
        SectionType.S45_Info -> "Misc. Info"
        SectionType.S46_Unknown -> "Unknown 46"
        SectionType.S49_SpellList -> "Spell List"
        SectionType.S4A_Path -> "Path"
        SectionType.S53_AbilityList -> "Ability List"
        SectionType.S54_WeaponTrace -> "Weapon Trace"
        SectionType.S5E_Blur -> "Blur"
        SectionType.S5D_BumpMap -> "Bump Map"
        SectionType.S5F_Unknown -> "Unknown"
    }
}